import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode, type OfType } from '@reconcept-platform/shared/data-access';
import {
  type CreateGoalCardInput,
  type CreateGoalCardMutation,
  type CreateGoalCardMutationVariables,
  type DestroyCardMutation,
  type DestroyCardMutationVariables,
  type DuplicateGoalCardMutation,
  type DuplicateGoalCardMutationVariables,
  type GoalCardFilter,
  type GoalCardItemQueryQuery,
  type GoalCardItemQueryQueryVariables,
  type GoalCardsForExportQueryQuery,
  type GoalCardsForExportQueryQueryVariables,
  type GoalCardsOverviewQueryQuery,
  type GoalCardsOverviewQueryQueryVariables,
  type GoalCardsQueryQuery,
  type GoalCardsQueryQueryVariables,
  type GoalCardWithCommentsQuery,
  type GoalCardWithCommentsQueryVariables,
  type GoalCardWithLinkedCardsQueryQuery,
  type GoalCardWithLinkedCardsQueryQueryVariables,
  type TimelineWithGoalCardsQueryQuery,
  type TimelineWithGoalCardsQueryQueryVariables,
  type UpdateGoalCardInput,
  type UpdateGoalCardMutation,
  type UpdateGoalCardMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { GoalCardLogsQuery } from '../log/log.queries';
import { ViewerWithCardsAndTasksQuery } from '../viewer/viewer.queries';
import { goalCardItemFragment } from './goal-card.fragments';
import {
  GoalCardItemQuery,
  GoalCardsForExportQuery,
  GoalCardsOverviewQuery,
  GoalCardsQuery,
  goalCardWithCommentsQuery,
  GoalCardWithLinkedCardsQuery,
  TimelineWithGoalCardsQuery,
} from './goal-card.queries';

export type GoalCardWithLinkedCardsQuery_LinkedCard = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  GoalCardWithLinkedCardsQueryQuery['node'],
  'GoalCard'
  // @ts-expect-error TS2537 cannot index
>['linkedCards'][number];

// @ts-expect-error TS2339 property does not exist
export type GoalCardsQuery_GoalCard = GoalCardsQueryQuery['goalCards']['nodes'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type GoalCardItemQuery_GoalCard = OfType<GoalCardItemQueryQuery['node'], 'GoalCard'>;
// @ts-expect-error TS2537 cannot index
export type GoalCardItemQuery_LinkedCard = GoalCardItemQuery_GoalCard['linkedCards'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type GoalCardWithCommentsQuery_GoalCard = OfType<GoalCardWithCommentsQuery['node'], 'GoalCard'>;

// @ts-expect-error TS2339 property does not exist
export type TimelineWithGoalCardsQuery_GoalCard = TimelineWithGoalCardsQueryQuery['goalCards']['nodes'][number];

// @ts-expect-error TS2339 property does not exist
export type GoalCardsOverviewQuery_GoalCard = GoalCardsOverviewQueryQuery['goalCards']['nodes'][number];

@Injectable({
  providedIn: 'root',
})
export class GoalCardApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchGoalCardWithSubDomain(goalCardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<GoalCardItemQueryQuery, GoalCardItemQueryQueryVariables>({
        query: GoalCardItemQuery,
        variables: {
          id: goalCardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'GoalCard')));
  }

  watchTimelineGoalCards(variables: TimelineWithGoalCardsQueryQueryVariables) {
    return (
      this.workspaceGraphQLService
        .watchQuery<TimelineWithGoalCardsQueryQuery, TimelineWithGoalCardsQueryQueryVariables>({
          query: TimelineWithGoalCardsQuery,
          variables,
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.goalCards.nodes))
    );
  }

  watchGoalCards(variables: GoalCardsQueryQueryVariables) {
    return (
      this.workspaceGraphQLService
        .watchQuery<GoalCardsQueryQuery, GoalCardsQueryQueryVariables>({
          query: GoalCardsQuery,
          variables,
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.goalCards.nodes))
    );
  }

  watchGoalCardsForExport(goalCardsFilter: GoalCardFilter) {
    return (
      this.workspaceGraphQLService
        .watchQuery<GoalCardsForExportQueryQuery, GoalCardsForExportQueryQueryVariables>({
          query: GoalCardsForExportQuery,
          variables: {
            workspaceIds: goalCardsFilter.workspaceIds,
            status: goalCardsFilter.status,
            severity: goalCardsFilter.severity,
            subDomainIds: goalCardsFilter.subDomainIds,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.goalCards.nodes))
    );
  }

  watchGoalCardOverview(goalCardsFilter: GoalCardFilter) {
    return (
      this.workspaceGraphQLService
        .watchQuery<GoalCardsOverviewQueryQuery, GoalCardsOverviewQueryQueryVariables>({
          query: GoalCardsOverviewQuery,
          variables: {
            workspaceIds: goalCardsFilter.workspaceIds,
            status: goalCardsFilter.status,
            severity: goalCardsFilter.severity,
            subDomainIds: goalCardsFilter.subDomainIds,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.goalCards.nodes))
    );
  }

  watchGoalWithLinkedCards(goalCardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<GoalCardWithLinkedCardsQueryQuery, GoalCardWithLinkedCardsQueryQueryVariables>({
        query: GoalCardWithLinkedCardsQuery,
        variables: {
          id: goalCardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'GoalCard')));
  }

  watchGoalCardWithComments(goalCardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<GoalCardWithCommentsQuery, GoalCardWithCommentsQueryVariables>({
        query: goalCardWithCommentsQuery,
        variables: {
          id: goalCardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'GoalCard')));
  }

  createGoalCard(createGoalCardInput: CreateGoalCardInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateGoalCardMutation, CreateGoalCardMutationVariables>({
          mutation: gql`
            mutation CreateGoalCard($input: CreateGoalCardInput!) {
              createGoalCard(input: $input) {
                goalCard {
                  ...GoalCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${goalCardItemFragment}
          `,
          variables: {
            input: createGoalCardInput,
          },
          update: (cache) => {
            cache.modify({
              fields: {
                goalCards(existingGoalCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
            cache.modify({
              id: createGoalCardInput.subDomainId,
              fields(fieldValue, { DELETE }) {
                return DELETE;
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createGoalCard.goalCard))
    );
  }

  updateGoalCard(updateGoalCardInput: UpdateGoalCardInput, subDomainId: string) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateGoalCardMutation, UpdateGoalCardMutationVariables>({
          mutation: gql`
            mutation UpdateGoalCard($input: UpdateGoalCardInput!) {
              updateGoalCard(input: $input) {
                goalCard {
                  ...GoalCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${goalCardItemFragment}
          `,
          variables: {
            input: updateGoalCardInput,
          },
          refetchQueries: [
            {
              query: GoalCardItemQuery,
              variables: {
                id: updateGoalCardInput.goalCardId,
              },
            },
            {
              query: GoalCardLogsQuery,
              variables: {
                goalCardIds: [updateGoalCardInput.goalCardId],
              },
            },
            {
              query: ViewerWithCardsAndTasksQuery,
            },
          ],
          update: (cache) => {
            cache.modify({
              fields: {
                goalCards(existingGoalCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
            cache.modify({
              id: subDomainId,
              fields(fieldValue, { DELETE }) {
                return DELETE;
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateGoalCard.goalCard))
    );
  }

  destroyCard(goalCardId: string) {
    return this.workspaceGraphQLService.mutateV2<DestroyCardMutation, DestroyCardMutationVariables>({
      mutation: gql`
        mutation DestroyCard($input: DestroyGoalCardInput!) {
          destroyGoalCard(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input: {
          goalCardId,
        },
      },
      update: (cache) => {
        cache.modify({
          fields: {
            goalCards(existingGoalCards, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
    });
  }

  duplicateGoalCard(goalCardId: string) {
    return (
      this.workspaceGraphQLService
        .mutateV2<DuplicateGoalCardMutation, DuplicateGoalCardMutationVariables>({
          mutation: gql`
            mutation DuplicateGoalCard($input: DuplicateGoalCardInput!) {
              duplicateGoalCard(input: $input) {
                goalCard {
                  ...GoalCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${goalCardItemFragment}
          `,
          variables: {
            input: {
              goalCardId,
            },
          },
          update: (cache) => {
            cache.modify({
              fields: {
                goalCards(existingGoalCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.duplicateGoalCard.goalCard))
    );
  }
}
